import React from "react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import CancelPlanSubscription from "components/CancelPlanSubscription";
import JarvisButton from "components/JarvisButton";
import { dateTimeHelper } from "helper/dateTime";
import { formatHelper } from "helper/format";
import {
  PLAN_STATUS_META,
  getFrequencyDisplay,
  getProductMeta,
} from "./billingHelpers";

const PREMIUM_PLAN_CODES = {
  PORTFOLIO: "JP0006",
  RMS: "JR0006",
  ONESTOCK: "JO0006",
};

/**
 * Figma 7942:93644 — Active plan summary for Manage Subscription.
 */
const ManagePlanCard = ({
  productCode,
  subscriptionDetails = {},
  customerSubscriptionId,
  reasonsDropdown = [],
  planDetails = {},
  onUpgrade,
  onRenew,
}) => {
  const meta = getProductMeta(productCode);
  const planStatus = subscriptionDetails.planStatus || "";
  const statusMeta = PLAN_STATUS_META[planStatus] || null;
  const isActive = planStatus === "ACTIVATED";
  const planName = subscriptionDetails.planName || meta.displayName;
  const frequency = getFrequencyDisplay(subscriptionDetails.paymentFrequency);
  const billingDate = subscriptionDetails.expiryDate
    ? dateTimeHelper.displayDate(subscriptionDetails.expiryDate)
    : "";
  const currentDate = dateTimeHelper.sipDate(dateTimeHelper.now());
  const currentYear = dateTimeHelper.format(currentDate, "$YYYY");
  const currentMonth = dateTimeHelper.format(currentDate, "$MM");
  const customerPlanExpiryDate = subscriptionDetails.expiryDate
    ? dateTimeHelper.sipDate(subscriptionDetails.expiryDate)
    : "";
  const customerPlanExpiryMonth = customerPlanExpiryDate
    ? dateTimeHelper.format(customerPlanExpiryDate, "$MM")
    : "";
  const customerPlanExpiryYear = customerPlanExpiryDate
    ? dateTimeHelper.format(customerPlanExpiryDate, "$YYYY")
    : "";
  const isPremiumPlan = Object.values(PREMIUM_PLAN_CODES).includes(
    planDetails?.planCode,
  );
  const inExpiryMonth =
    currentMonth === customerPlanExpiryMonth &&
    currentYear === customerPlanExpiryYear;
  const isExpired = planStatus === "EXPIRED";
  const showUpgrade = isActive && !!onUpgrade && !isPremiumPlan;
  const showRenew =
    (isExpired && !!onRenew) ||
    (isActive && !!onUpgrade && isPremiumPlan && inExpiryMonth);

  const cancelTrigger = (onOpen) => (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      sx={{
        border: 0,
        bgcolor: "transparent",
        p: 0,
        cursor: "pointer",
        alignSelf: { xs: "flex-start", sm: "flex-end" },
      }}
    >
      <Text
        sx={{
          fontWeight: 500,
          fontSize: 14,
          color: "#9CA3AF",
          textDecoration: "underline",
          textUnderlineOffset: "3px",
          "&:hover": { color: "#6B7280" },
        }}
      >
        Cancel Subscription
      </Text>
    </Box>
  );

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: { xs: "14px", md: "20px" },
        boxShadow: "0px 4px 10px rgba(0,0,0,0.02)",
        p: { xs: 2, md: 4 },
      }}
    >
      <Stack spacing={{ xs: 2.5, md: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "flex-start" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              component="img"
              src={meta.icon}
              alt=""
              sx={{
                width: { xs: 52, md: 70 },
                height: { xs: 54, md: 72 },
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
              >
                <Text
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 16, md: 20 },
                    color: "#111827",
                    lineHeight: 1.3,
                  }}
                >
                  {planName}
                </Text>
                {statusMeta ? (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      px: 1,
                      py: 0.35,
                      borderRadius: "8px",
                      border: `1px solid ${statusMeta.border}`,
                      bgcolor: statusMeta.bg,
                    }}
                  >
                    {isActive ? (
                      <CheckRoundedIcon
                        sx={{ fontSize: 14, color: statusMeta.color }}
                      />
                    ) : null}
                    <Text
                      sx={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: statusMeta.color,
                        lineHeight: 1.3,
                      }}
                    >
                      {statusMeta.label}
                    </Text>
                  </Stack>
                ) : null}
              </Stack>
              {meta.tagline ? (
                <Text sx={{ fontSize: 14, color: "rgba(0,0,0,0.7)" }}>
                  {meta.tagline}
                </Text>
              ) : null}
            </Stack>
          </Stack>

          {billingDate ? (
            <Text
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: "#B4B4B4",
                flexShrink: 0,
                whiteSpace: { sm: "nowrap" },
              }}
            >
              Next Billing Date: {billingDate}
            </Text>
          ) : null}
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "flex-end" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Text
              sx={{
                fontWeight: 800,
                fontSize: { xs: 24, md: 32 },
                color: "#111827",
                lineHeight: 1.1,
              }}
            >
              {subscriptionDetails.planPrice != null &&
              subscriptionDetails.planPrice !== ""
                ? formatHelper.currency(subscriptionDetails.planPrice)
                : formatHelper.currency(0)}
            </Text>
            {frequency ? (
              <Text
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: 14, md: 16 },
                  color: "#9CA3AF",
                }}
              >
                /{frequency}
              </Text>
            ) : null}
          </Stack>

          <Stack
            spacing={showUpgrade || showRenew ? 1 : 0}
            alignItems={{ xs: "flex-start", sm: "flex-end" }}
          >
            {showUpgrade || showRenew ? (
              <JarvisButton
                onClick={isExpired ? onRenew : onUpgrade}
                disableEndIcon
                variant="outlined"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 156 },
                }}
              >
                {showRenew ? "Renew Plan" : "Upgrade Plan"}
              </JarvisButton>
            ) : null}

            {isActive && customerSubscriptionId ? (
              <CancelPlanSubscription
                customerSubscriptionId={customerSubscriptionId}
                reasonsDropdown={reasonsDropdown}
                openComponent={cancelTrigger}
              />
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ManagePlanCard;
